import { useEffect, useId, useState } from 'react'
import {
  grantStagingAccess,
  hasValidStagingAccess,
  isStagingPasswordConfigured,
  isStagingProtectionEnabled,
  revokeStagingAccess,
  verifyStagingPassword,
} from '../utils/stagingAccess'
import StagingBanner from './StagingBanner'

/**
 * Portail mot de passe pour staging.n-g.fr.
 * S’affiche si le domaine (ou VITE_STAGING_PROTECTION=true) active la protection.
 * Production (n-g.fr) n’est pas concernée.
 */
export default function StagingAccessGate({ children }) {
  const passwordId = useId()
  const protectionEnabled = isStagingProtectionEnabled()
  const passwordConfigured = isStagingPasswordConfigured()
  const [unlocked, setUnlocked] = useState(() => {
    if (!protectionEnabled) return true
    if (!passwordConfigured) return false
    return hasValidStagingAccess()
  })
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!protectionEnabled) return undefined

    const previousTitle = document.title
    document.title = 'Staging — Nature & Gîtes'

    const existingMeta = document.querySelector('meta[name="robots"]')
    const previousContent = existingMeta?.getAttribute('content')
    let createdMeta = null

    if (!existingMeta) {
      createdMeta = document.createElement('meta')
      createdMeta.setAttribute('name', 'robots')
      document.head.appendChild(createdMeta)
    }

    const meta = existingMeta || createdMeta
    if (meta) meta.setAttribute('content', 'noindex, nofollow, noarchive')

    return () => {
      document.title = previousTitle
      if (existingMeta) {
        if (previousContent) existingMeta.setAttribute('content', previousContent)
        else existingMeta.removeAttribute('content')
      } else if (createdMeta) {
        createdMeta.remove()
      }
    }
  }, [protectionEnabled])

  const handleLogout = () => {
    revokeStagingAccess()
    setPassword('')
    setError('')
    setSubmitting(false)
    setUnlocked(false)
  }

  if (unlocked) {
    if (!protectionEnabled) return children

    return (
      <>
        <StagingBanner onLogout={handleLogout} />
        {children}
      </>
    )
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!passwordConfigured) return

    setSubmitting(true)
    setError('')

    if (!verifyStagingPassword(password)) {
      setError('Mot de passe incorrect.')
      setPassword('')
      setSubmitting(false)
      return
    }

    if (!grantStagingAccess()) {
      setError('Impossible d’enregistrer la session. Réessayez.')
      setSubmitting(false)
      return
    }

    setUnlocked(true)
    setSubmitting(false)
  }

  return (
    <div className="min-h-screen bg-ng-beige flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-[2rem] border border-ng-dark/10 bg-white/90 p-8 shadow-[0_30px_90px_rgba(0,0,0,0.08)] backdrop-blur-sm sm:p-10">
        <div className="mb-8 space-y-3 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ng-brown">
            Environnement de développement
          </p>
          <h1 className="font-display text-3xl font-bold text-ng-dark sm:text-4xl">
            Accès staging
          </h1>
          <p className="text-sm leading-relaxed text-ng-dark/70 sm:text-base">
            {passwordConfigured
              ? 'Ce site est réservé à l’équipe. Saisissez le mot de passe pour continuer.'
              : 'Accès verrouillé : le mot de passe staging n’est pas configuré pour ce build (VITE_STAGING_PASSWORD).'}
          </p>
        </div>

        {passwordConfigured ? (
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div className="space-y-2">
              <label htmlFor={passwordId} className="block text-sm font-medium text-ng-dark">
                Mot de passe
              </label>
              <input
                id={passwordId}
                name="staging-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value)
                  if (error) setError('')
                }}
                className="w-full rounded-2xl border border-ng-dark/15 bg-ng-beige/60 px-4 py-3 text-base text-ng-dark outline-none transition-colors focus:border-ng-dark/40 focus:ring-2 focus:ring-ng-dark/20"
                placeholder="••••••••"
                required
              />
              {error ? (
                <p className="text-sm text-red-700" role="alert">
                  {error}
                </p>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={submitting || password.length === 0}
              className="inline-flex w-full items-center justify-center rounded-full bg-ng-dark px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ng-mid focus:outline-none focus:ring-2 focus:ring-ng-dark focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Entrer sur le site
            </button>
          </form>
        ) : (
          <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
            Définissez <code className="font-mono text-xs">VITE_STAGING_PASSWORD</code> avant le build déployé sur staging.n-g.fr.
          </p>
        )}

        <p className="mt-6 text-center text-xs text-ng-dark/50">
          staging.n-g.fr · Nature &amp; Gîtes
        </p>
      </div>
    </div>
  )
}
